import type { UserRole } from "@blog/spa-common/roles";
import Dexie, { type EntityTable } from "dexie";
import type { CommentsMode } from "@/lib/posts";

export type Visibility = "public" | "authenticated" | "role" | "users";
// S-3: catálogo desde @blog/spa-common/roles (espejo verificado del
// USER_ROLES del servidor). `autor` faltaba aquí desde la 0047, así que el
// picker no lo ofrecía y el Zod del servidor tampoco lo aceptaba.
export type VisibilityRole = UserRole;
// Editorial review (migration 0047). Staff posts stay 'approved'; author
// posts move draft → pending → approved | changes_requested.
export type ReviewStatus = "draft" | "pending" | "changes_requested" | "approved";

export interface LocalPost {
  id: number;             // negative = unsaved local draft
  title: string;
  slug: string;
  excerpt: string;
  contentMd: string;
  coverImage: string;
  published: boolean;
  // Transient publish-time preference (not returned by the API): when false,
  // publishing this post skips the newsletter blast. Optional/undefined =
  // notify (the previous default). Only matters on the draft→publish step.
  notifySubscribers?: boolean;
  commentsMode: CommentsMode;
  hasPassword: boolean;
  pendingPasswordMode?: "unchanged" | "set" | "clear";
  pendingPasswordValue?: string;
  tags: string[];
  scheduledAt: string | null;
  featured: boolean;
  authorId: number | null;
  // Row-level visibility ACL (migration 0028).
  visibility: Visibility;
  visibilityRole: VisibilityRole | null;
  aclUserIds: number[];
  // Series grouping (migration 0026). All three null = not part of a series.
  seriesSlug: string | null;
  seriesPosition: number | null;
  seriesTitle: string | null;
  // Paywall (migration 0039). Price in cents; only meaningful on a locked
  // (password-protected) post. null = free / not for sale.
  priceCents: number | null;
  // Incluido en el pase premium (migración 0067). El post se anuncia igual;
  // lo que se retiene a quien no tiene pase es el CUERPO. Excluyente con
  // `priceCents`: o se vende suelto o entra en el pase (el servidor lo
  // rechaza con `premium_and_price`).
  premium: boolean;
  // Editorial review state (migration 0047). Read-only from the author's
  // side (changed via submit/approve/request-changes, not the autosave PUT).
  reviewStatus: ReviewStatus;
  updatedAt: number;      // unix ms
  dirty: 0 | 1;
  deleted: 0 | 1;
}

export interface PendingOp {
  id?: number;
  postId: number;
  kind: "create" | "update" | "delete" | "feature" | "duplicate";
  payload: Record<string, unknown> | null;
  attempts: number;
  createdAt: number;
  lastError: string | null;
}

export interface MetaRow {
  key: "lastHydratedAt" | "lastSyncAt";
  value: number;
}

class PostsDB extends Dexie {
  posts!: EntityTable<LocalPost, "id">;
  queue!: EntityTable<PendingOp, "id">;
  meta!: EntityTable<MetaRow, "key">;

  constructor() {
    super("posts-app");
    this.version(1).stores({
      posts: "id, title, slug, published, featured, dirty, deleted, updatedAt",
      queue: "++id, postId, kind, attempts, createdAt",
      meta:  "&key",
    });
    // Migration 0028 — backfill visibility/ACL fields on existing local rows
    // so the new MetaRailVisibility doesn't read `undefined` from Dexie.
    this.version(2).upgrade(async (tx) => {
      await tx.table("posts").toCollection().modify((p: LocalPost) => {
        if (p.visibility === undefined) p.visibility = "public";
        if (p.visibilityRole === undefined) p.visibilityRole = null;
        if (p.aclUserIds === undefined) p.aclUserIds = [];
      });
    });
    // Series (0026) + paywall (0039) fields — backfill null on existing rows
    // so MetaRailSeries / MetaRailPaywall never read `undefined` from Dexie.
    this.version(3).upgrade(async (tx) => {
      await tx.table("posts").toCollection().modify((p: LocalPost) => {
        if (p.seriesSlug === undefined) p.seriesSlug = null;
        if (p.seriesPosition === undefined) p.seriesPosition = null;
        if (p.seriesTitle === undefined) p.seriesTitle = null;
        if (p.priceCents === undefined) p.priceCents = null;
      });
    });
    // Review status (0047) — existing local rows are staff-authored, so they
    // backfill to 'approved' (the no-review-needed state).
    this.version(4).upgrade(async (tx) => {
      await tx.table("posts").toCollection().modify((p: LocalPost) => {
        if (p.reviewStatus === undefined) p.reviewStatus = "approved";
      });
    });
    // Pase premium (0067) — las filas locales existentes no son premium.
    // Sin el relleno, el interruptor leería `undefined` de Dexie y saldría
    // apagado pero "no controlado", que en React es un aviso y un cambio de
    // valor a mitad de sesión.
    this.version(5).upgrade(async (tx) => {
      await tx.table("posts").toCollection().modify((p: LocalPost) => {
        if (p.premium === undefined) p.premium = false;
      });
    });
  }
}

export const db = new PostsDB();
